import axios from 'axios';
import React, { useEffect, useState } from 'react';
import SessionStorage from '../../utils/SessionStorage';
import { IDigitalBankTerms, IRate } from '../../types/shared/DigitalBankTerms';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { safeParse } from '../../utils/cob-utils';


interface IChildren {
    children: React.ReactNode;
}

export interface ITermRate {
    code?: string,
    name?: string,
    accountType?: string,
    group?: string,
    subType?: string,
    type?: string,
    term?: string,
    apy?: string,
    interestRate?: string,
    preferredRate?: boolean,
    featuredRate?: boolean,
    link?: string
    loyaltyRate?: string;
    loyaltyReward?: string;
}

export interface ITermRateKinds {
    jumboCD: ITermRate[],
    bumpupCD: ITermRate[],
    savings: ITermRate[],
}

interface ITermRatesContextValue {
    termRates: IDigitalBankTerms;
    getFeaturedItem: ( productType : 'jumboCd' | 'bumpUpCd' | 'highYieldSavings' ) => IRate
}

export const TermRatesContext = React.createContext<ITermRatesContextValue>({} as ITermRatesContextValue);

export const useTermRatesContext = () => React.useContext(TermRatesContext);

export const TermRatesProvider = (props:IChildren) => {
    const [termRates, setTermRates] = useState<IDigitalBankTerms>({});

    const getLowestApy = (rates: IRate[]): IRate | undefined => {
    const lowestRate = rates.reduce((lowest, current) =>Number(current.apy) < Number(lowest.apy) ? current : lowest);
        return {
        ...lowestRate,
        featuredRate: true,
        };
    };
    
    useEffect(() => {
        if (window.location.href.includes('deposits') && !SessionStorage.contains('TermsRates')) {
            axios.get(getDigitalBankApiUrl()).then((response : {data : ITermRateKinds}) => {
                SessionStorage.setObject('TermsRates', response.data);
                setTermRates(formatData(response.data));
            }).catch(() => {
                // eslint-disable-next-line no-console
                console.log('Using CF term data');
                setTermRates(JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string));
            });
        } else if (SessionStorage.contains('TermsRates')) {
            setTermRates(formatData(SessionStorage.getObject('TermsRates')));
        }
    }, []);


    const formatData = (data : ITermRateKinds) => {
        const trackingParam = PathUtils.getMetaPropertyValue('digitalBankAppSource');
        const digitalbankTermsData = safeParse(PathUtils.getMetaPropertyValue('digitalBankTerms'));
        const formattedData: IDigitalBankTerms = {
            jumboCd: data.jumboCD,
            bumpUpCd: data.bumpupCD,
            highYieldSavings: data.savings,
        };

        Object.keys(formattedData).forEach((accountType) => {
            (formattedData[accountType as keyof IDigitalBankTerms] as ITermRate[])?.forEach((term : ITermRate, index : number) => {
                const termUrl = digitalbankTermsData?.baseUrl ?  new URL(digitalbankTermsData.baseUrl as any) : null; // any used to resolve test-ssr failure to build
                const useNewUrlsFlag = PathUtils.getMetaPropertyValue('useNewUrls') === 'true'
                if(useNewUrlsFlag){
                    termUrl?.searchParams?.set('productCode', term.group);
                    if(term.term){
                        termUrl?.searchParams?.set('term', term.term);
                    }
                    termUrl?.searchParams?.set('source', trackingParam);
                } else {
                    termUrl?.searchParams?.set('productId', term.code);
                    termUrl?.searchParams?.set('app_source', trackingParam);
                }
                

                const formattedTerm : IRate = {
                    apr: term.apy,
                    featuredRate: term.preferredRate ?? term.featuredRate,
                    term: term.term,
                    apy: String((Number(term.apy) ?? 0.00).toFixed(2)),
                    loyaltyRate: term.loyaltyRate,
                    loyaltyReward: term.loyaltyReward,
                    url: termUrl ? termUrl.toString() : '',
                    group: term.group,
                };
                formattedData[accountType as keyof IDigitalBankTerms][index] = { ...formattedTerm };
            });
        });

        return formattedData;
    };

    const getFeaturedItem = (productType : 'jumboCd' | 'bumpUpCd' | 'highYieldSavings' ) => {
        let featuredItem = (termRates[productType] as IRate[])?.find((rate) => rate.featuredRate);

        if (featuredItem === undefined && Object.keys(termRates).length !== 0) {
            featuredItem = getLowestApy(termRates[productType] as IRate[]);
            console.error('no featured item found for ' + productType);
        } else if (featuredItem === undefined) {
            return {};
        }

        return featuredItem;
    };

    const getDigitalBankApiUrl = () => {
        return JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string).apiUrl;
    };

    return (
        <TermRatesContext.Provider value={{ termRates, getFeaturedItem }}>
            {props.children}
        </TermRatesContext.Provider>
    );
};

export default TermRatesProvider;